import { Input } from 'antd';
import { TaskType } from '../../../types/TaskType';
import colorTokens from '../../../constants/design_system/colors';
import { useState, useRef, useEffect, useMemo } from 'react';
import Task from '../../../types/Task';
import { TaskTypeLabel } from './TaskTypeDropdown';
import { Epic } from '../../../types/Epic';
import { CloseOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import CustomTooltip from '../Tooltip/CustomTooltip';
import { GoToIcon } from '../../../assets/CustomIcons/GoToIcon';
import './ItemDropdown.scss';
import NoDragWrapper from './NoDragWrapper';
import { useCreateTask } from '../../../serverState/tasks';
import { useGetActualSpaceId, useGetWorkspace } from '../../../serverState/workspace';
import { queryClient } from '../../../queryClient';
import BaseDropdown from './BaseDropdown';

export type ParentTaskDropdownProps = {
  task: Task | null;
  options: Task[];
  epics: Epic[];
  onSelect?: (task?: Task) => void;
  onBlur?: () => void;
  disabled?: boolean;
  onOpenParentTask?: (task: Task) => void;
  size?: 'small' | 'medium' | 'large';
};

const ParentTaskDropdown = (props: ParentTaskDropdownProps) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [scrollKey, setScrollKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const size = props.size ?? 'large';
  const createTaskMutation = useCreateTask();
  const spaceId = useGetActualSpaceId();
  const workspaceQuery = useGetWorkspace();
  const searchInputRef = useRef<any>(null);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      // Small delay to ensure dropdown is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isDropdownOpen]);

  const groupTasksByEpic = () => {
    const { options, epics } = props;
    const taskOrder = workspaceQuery.data?.TaskOrder[0];
    const tasksOrderMap = new Map<number, number>(taskOrder?.taskIds?.map((id: number, index: number) => [id, index]));
    const selectedTaskId = props.task?.id;

    // Create a map of epic IDs to their corresponding tasks
    const epicTasksMap: Record<string, Task[]> = {};

    // Initialize with "No Epic" group for tasks without an epic
    epicTasksMap['no-epic'] = [];

    // Group tasks by epic ID
    options.forEach((task) => {
      if (task.epicId) {
        if (!epicTasksMap[task.epicId]) {
          epicTasksMap[task.epicId] = [];
        }
        epicTasksMap[task.epicId].push(task);
      } else {
        epicTasksMap['no-epic'].push(task);
      }
    });

    // Sort tasks within each epic with the selected task first, then by tasksOrderMap
    Object.keys(epicTasksMap).forEach((epicId) => {
      epicTasksMap[epicId].sort((a, b) => {
        const aSelected = a.id === selectedTaskId;
        const bSelected = b.id === selectedTaskId;
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;

        const aIndex = tasksOrderMap.get(a.id);
        const bIndex = tasksOrderMap.get(b.id);

        if (aIndex !== undefined && bIndex !== undefined) {
          return aIndex - bIndex;
        }

        return a.id - b.id;
      });
    });

    return epicTasksMap;
  };

  const onSelect = (task: Task) => {
    if (props.onSelect) {
      props.onSelect(task);
    }
    isDropdownOpen && setDropdownOpen(false);
    setSearchTerm('');
  };

  // Sort epic groups with the selected task's epic first, and freeze both this
  // and the task grouping/sort above while the dropdown stays open, only
  // recomputing on open/close — matches the Label/Epic dropdown convention.
  const sortedEpics = useMemo(() => {
    const selectedEpicId = props.task?.epicId;
    return [...props.epics].sort((a, b) => {
      const aSelected = selectedEpicId === a.id;
      const bSelected = selectedEpicId === b.id;
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  }, [isDropdownOpen, props.epics]);

  const epicTasksMap = useMemo(() => groupTasksByEpic(), [isDropdownOpen, props.options, workspaceQuery.data]);

  // Filter tasks based on search term
  const filteredEpicTasksMap: Record<string, Task[]> = {};
  Object.entries(epicTasksMap).forEach(([epicId, tasks]) => {
    const filteredTasks = tasks.filter((task) => task.title.toLowerCase().includes(searchTerm.toLowerCase()));

    if (filteredTasks.length > 0) {
      filteredEpicTasksMap[epicId] = filteredTasks;
    }
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleCreateParentTask = async () => {
    if (!searchTerm.trim()) return;

    setDropdownOpen(false);
    setSearchTerm('');

    const createdTask = await createTaskMutation.mutateAsync({
      workspaceId: Number(spaceId),
      title: searchTerm.trim(),
      type: TaskType.task,
    });

    await queryClient.invalidateQueries({ queryKey: ['tasks', Number(spaceId)] });

    onSelect(createdTask);
  };

  return (
    <NoDragWrapper className={`h-full ${props.disabled ? 'cursor-not-allowed' : ''}`}>
      <CustomTooltip title={props.disabled ? 'You can not add a parent for an Item with Subitems.' : ''}>
        <BaseDropdown
          disableTransition
          trigger={['click']}
          open={isDropdownOpen}
          disabled={props.disabled}
          onOpenChange={(open) => {
            if (open) setScrollKey((k) => k + 1);
            setDropdownOpen(open);
            if (!open) {
              setSearchTerm('');
            }
          }}
          dropdownRender={() => (
            <div key={scrollKey} className="flex flex-col items-start justify-start bg-brand-white shadow-md rounded-lg px-2 max-h-[450px] overflow-y-auto">
              {/* Search input */}
              <div className="w-full mb-2 sticky top-0 z-10 bg-brand-white pt-2">
                <Input
                  ref={searchInputRef}
                  placeholder="Search for Parent..."
                  prefix={<SearchOutlined className="mr-1" />}
                  allowClear={searchTerm.length > 0}
                  className="[&_input::placeholder]:!text-neutral-900 px-2 py-1 text-neutral-900 mr-auto w-[188px]"
                  value={searchTerm}
                  onChange={handleSearch}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
              </div>

              {/* Render tasks grouped by epics */}
              {sortedEpics.map((epic) => {
                const tasks = filteredEpicTasksMap[epic.id];
                if (!tasks || tasks.length === 0) return null;

                return (
                  <div key={epic.id} className="w-full">
                    <div
                      className="text-neutral-900 text-xs font-semibold py-1 w-full max-w-[180px] truncate overflow-hidden whitespace-nowrap"
                      title={epic.title}
                    >
                      {epic.title}
                    </div>
                    {tasks.map((task) => (
                      <ParentTaskLabel key={task.id} task={task} size="large" onClick={() => onSelect(task)} />
                    ))}
                  </div>
                );
              })}

              {/* Render "No Epic" group last */}
              {filteredEpicTasksMap['no-epic']?.length > 0 && (
                <div className="w-full">
                  <div className="text-neutral-900 text-xs font-semibold py-1 w-full">No Epic</div>
                  {filteredEpicTasksMap['no-epic'].map((task) => (
                    <ParentTaskLabel key={task.id} task={task} size="large" onClick={() => onSelect(task)} />
                  ))}
                </div>
              )}

              {/* Show message when no results */}
              {Object.keys(filteredEpicTasksMap).length === 0 && (
                <div className="justify-start text-neutral-900 text-xs font-semibold">No Results</div>
              )}

              {/* Create new parent task option when searching */}

              {searchTerm.trim() && (
                <div className="w-full sticky bottom-0 bg-brand-white pb-2">
                  <div className="w-full h-px bg-neutral-300 mb-1"></div>
                  <div
                    className="w-[180px] h-full p-1 flex items-center gap-2 cursor-pointer hover:bg-neutral-200"
                    onClick={handleCreateParentTask}
                  >
                    <div className="h-4 w-4 justify-center items-center flex shrink-0 text-neutral-900 rounded">
                      <PlusOutlined className="text-xs" />
                    </div>
                    <div className="py-1 text-neutral-1000 text-sm font-normal leading-tight truncate overflow-hidden whitespace-nowrap">
                      {`Create "${searchTerm.trim()}"`}
                    </div>
                  </div>
                </div>
              )}

              {/* Divider and Remove Parent option — pinned to the bottom like
                  "Remove Epic" so it's reachable without scrolling the list (#2395) */}
              {props.task && props.onSelect && !searchTerm.trim() && (
                <div className="w-full sticky bottom-0 bg-brand-white pb-2">
                  <div className="w-full h-px bg-neutral-300 mb-1"></div>
                  <div
                    className="w-[180px] h-full p-1 flex items-center gap-2 cursor-pointer hover:bg-neutral-200"
                    onClick={() => {
                      if (props.onSelect) {
                        props.onSelect(undefined);
                        setDropdownOpen(false);
                        setSearchTerm('');
                      }
                    }}
                  >
                    <div className="h-4 w-4 justify-center items-center flex shrink-0 text-neutral-900 rounded">
                      <CloseOutlined className="text-xs" />
                    </div>
                    <div className="py-1 text-neutral-900 text-sm font-normal leading-tight">Remove Parent</div>
                  </div>
                </div>
              )}
            </div>
          )}
        >
          <div
            className={`h-full flex items-center ${props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            style={{ outline: isDropdownOpen ? `2px solid ${colorTokens.green[300]}` : 'none' }}
          >
            {props.task ? (
              <div className="relative parent-task">
                <ParentTaskLabel task={props.task} size={size} />
                {size === 'large' && props.onOpenParentTask && (
                  <GoToIcon
                    className="absolute right-1 top-1 text-neutral-900 bg-neutral-300 rounded p-1 parent-task__go-to-icon cursor-pointer"
                    onClick={(e: React.MouseEvent<SVGSVGElement>) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (props.onOpenParentTask && props.task) {
                        setDropdownOpen(false);
                        props.onOpenParentTask(props.task);
                      }
                    }}
                  />
                )}
              </div>
            ) : (
              <CustomTooltip title={props.disabled !== true ? 'Adding Parent converts Item to Subitem' : ''}>
                <div className="w-full h-full flex items-center justify-center">
                  <div className="px-1 text-neutral-900 text-sm font-normal">Add Parent</div>
                </div>
              </CustomTooltip>
            )}
          </div>
        </BaseDropdown>
      </CustomTooltip>
    </NoDragWrapper>
  );
};

export const ParentTaskLabel = (props: {
  task: Pick<Task, 'type' | 'title'>;
  size: 'small' | 'medium' | 'large';
  onClick?: () => void;
}) => {
  return (
    <div
      className={`${props.size === 'large' ? 'w-[180px] py-1' : props.size === 'medium' ? 'w-[120px]' : 'max-w-[210px]'} h-full flex items-center gap-1 cursor-pointer hover:bg-neutral-200`}
      onClick={props.onClick}
    >
      <TaskTypeLabel type={props.task.type as TaskType} size="small" />
      <div className="py-1 text-neutral-900 text-sm font-normal leading-tight hover:text-brand-grey-dark truncate overflow-hidden whitespace-nowrap">
        {props.task.title}
      </div>
    </div>
  );
};

export default ParentTaskDropdown;
